import { useEffect, useMemo, useState } from "react"

import { Icon } from "@/components/Icon"
import { useToast } from "@/hooks/use-toast"
import { safeHref } from "@/lib/safe-url"

import type { CitationReview } from "../lib/review"
import {
  resolvePrecedent,
  searchPrecedents,
  verifyPrecedent,
  type PrecedentCandidate,
  type PrecedentVerdict,
} from "../services/precedents.service"
import { useReviewStore } from "../store/review.store"

interface CitationReviewModalProps {
  threadId: string
  allegationId: string
  /** "5.N" — where a rewrite would incorporate an attached authority. */
  sectionHint: string
  citations: CitationReview[]
  /** Open directly in add-a-new-authority mode. */
  addMode?: boolean
  onClose: () => void
}

/**
 * The officer's manual IndianKanoon loop: search → pick → deterministic verify
 * → attach as officer-confirmed (STRICT: a mismatched record can never attach)
 * or discard, plus an add-a-new-authority mode held to the same verify bar.
 * Prose is never touched here — attaching updates the review layer; the
 * officer incorporates it into the order text via the existing Rewrite flow.
 */
export function CitationReviewModal({
  threadId,
  allegationId,
  sectionHint,
  citations,
  addMode = false,
  onClose,
}: CitationReviewModalProps) {
  const { toast } = useToast()
  const applyResolve = useReviewStore((s) => s.applyResolve)

  const [mode, setMode] = useState<"resolve" | "add">(addMode ? "add" : "resolve")
  const [activeCitation, setActiveCitation] = useState<CitationReview | null>(
    citations[0] ?? null,
  )
  const [queryCitation, setQueryCitation] = useState(citations[0]?.citation ?? "")
  const [queryName, setQueryName] = useState(citations[0]?.case_name ?? "")
  const [candidates, setCandidates] = useState<PrecedentCandidate[]>([])
  const [verdicts, setVerdicts] = useState<Record<string, PrecedentVerdict>>({})
  const [searching, setSearching] = useState(false)
  const [busyDocId, setBusyDocId] = useState("")
  const [error, setError] = useState("")

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  const selectCitation = (citation: CitationReview) => {
    setActiveCitation(citation)
    setQueryCitation(citation.citation)
    setQueryName(citation.case_name)
    setCandidates([])
    setVerdicts({})
    setError("")
  }

  const switchMode = (next: "resolve" | "add") => {
    setMode(next)
    setCandidates([])
    setVerdicts({})
    setError("")
    if (next === "add") {
      setQueryCitation("")
      setQueryName("")
    } else if (activeCitation) {
      selectCitation(activeCitation)
    }
  }

  const runSearch = async () => {
    setSearching(true)
    setError("")
    setCandidates([])
    setVerdicts({})
    try {
      const results = await searchPrecedents({
        raw_citation: queryCitation,
        case_name: queryName,
        limit: 8,
      })
      setCandidates(results)
      if (results.length === 0) setError("No records found — refine the citation or the case name.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "IndianKanoon search failed.")
    } finally {
      setSearching(false)
    }
  }

  const runVerify = async (candidate: PrecedentCandidate) => {
    setBusyDocId(candidate.doc_id)
    setError("")
    try {
      const verdict = await verifyPrecedent({
        raw_citation: mode === "resolve" ? activeCitation?.citation ?? queryCitation : queryCitation,
        case_name: mode === "resolve" ? activeCitation?.case_name ?? queryName : queryName,
        doc_id: candidate.doc_id,
        source: candidate.source,
      })
      setVerdicts((prev) => ({ ...prev, [candidate.doc_id]: verdict }))
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed.")
    } finally {
      setBusyDocId("")
    }
  }

  const runResolve = async (
    action: "confirm" | "discard" | "add",
    candidate?: PrecedentCandidate,
  ) => {
    setBusyDocId(candidate?.doc_id ?? action)
    setError("")
    try {
      const response = await resolvePrecedent({
        thread_id: threadId,
        allegation_id: allegationId,
        action,
        raw_citation: action === "add" ? queryCitation : activeCitation?.citation ?? "",
        case_name: action === "add" ? queryName : activeCitation?.case_name ?? "",
        doc_id: candidate?.doc_id ?? "",
        source: candidate?.source ?? "indiankanoon",
      })
      applyResolve(allegationId, response.citation_reviews)
      const verb =
        action === "confirm" ? "confirmed" : action === "discard" ? "discarded" : "added"
      toast({
        title: `Citation ${verb}`,
        description:
          action === "discard"
            ? "The citation is recorded as discarded in the review trail."
            : `Verified and attached as officer-confirmed. To rely on it in the order text, use Rewrite on section ${sectionHint}.`,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : "The action could not be completed.")
    } finally {
      setBusyDocId("")
    }
  }

  const unresolved = useMemo(
    () => citations.filter((c) => c.citation),
    [citations],
  )

  return (
    <div className="modal-bg" onClick={onClose}>
      <div
        className="confirm"
        style={{ maxWidth: 760, width: "min(760px, calc(100vw - 32px))" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Citation review"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <h3 style={{ margin: 0 }}>Citation review — {allegationId}</h3>
          <div style={{ flex: 1 }} />
          <button
            className={"chip" + (mode === "resolve" ? " chip--ok" : "")}
            onClick={() => switchMode("resolve")}
            disabled={unresolved.length === 0}
          >
            Resolve cited case
          </button>
          <button
            className={"chip" + (mode === "add" ? " chip--ok" : "")}
            onClick={() => switchMode("add")}
          >
            Add authority
          </button>
          <button className="modal__close" onClick={onClose} aria-label="Close" style={{ position: "static" }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <p style={{ marginTop: 0, color: "var(--ink-2)" }}>
          {mode === "resolve"
            ? "Search IndianKanoon for the record behind the noticee's citation. A selection attaches only if it passes the same deterministic verification (name, court, year) the drafting agent uses."
            : "Search IndianKanoon for a supporting authority to add. It attaches only after passing the same deterministic verification — never as free text."}
        </p>

        {mode === "resolve" && unresolved.length > 1 && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            {unresolved.map((c) => (
              <button
                key={c.citation}
                className={"chip" + (activeCitation?.citation === c.citation ? " chip--ok" : "")}
                onClick={() => selectCitation(c)}
              >
                {c.citation}
              </button>
            ))}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            className="input"
            style={{ flex: "1 1 200px" }}
            placeholder="Citation (e.g. 29 Centax 395 (KER))"
            aria-label="Citation"
            value={queryCitation}
            onChange={(e) => setQueryCitation(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void runSearch()
            }}
          />
          <input
            className="input"
            style={{ flex: "1 1 200px" }}
            placeholder="Case name (e.g. X vs State of Kerala)"
            aria-label="Case name"
            value={queryName}
            onChange={(e) => setQueryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void runSearch()
            }}
          />
          <button
            className="btn btn--primary"
            onClick={runSearch}
            disabled={searching || (!queryCitation.trim() && !queryName.trim())}
          >
            {searching ? "Searching…" : "Search IndianKanoon"}
          </button>
        </div>

        {error && (
          <div style={{ color: "var(--upheld)", marginBottom: 10 }} role="alert">
            {error}
          </div>
        )}

        <div style={{ maxHeight: 320, overflowY: "auto", display: "grid", gap: 8 }}>
          {candidates.map((candidate) => {
            const verdict = verdicts[candidate.doc_id]
            const verified = verdict?.verification_status === "verified"
            return (
              <div
                key={candidate.doc_id}
                style={{
                  border: "1px solid var(--line-2)",
                  borderRadius: "var(--radius)",
                  padding: "10px 12px",
                  background: "var(--paper-2)",
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                  <strong style={{ flex: 1 }}>{candidate.title}</strong>
                  <span className="mono" style={{ color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                    {candidate.court}
                    {candidate.year ? ` · ${candidate.year}` : ""}
                  </span>
                </div>
                {candidate.snippet && (
                  <p style={{ margin: "6px 0", color: "var(--ink-2)" }}>{candidate.snippet}</p>
                )}
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {safeHref(candidate.url) && (
                    <a
                      href={safeHref(candidate.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mono"
                      style={{ color: "var(--gold)", fontSize: 12 }}
                    >
                      View on IndianKanoon
                    </a>
                  )}
                  <div style={{ flex: 1 }} />
                  {!verdict && (
                    <button
                      className="btn btn--ghost btn--sm"
                      onClick={() => runVerify(candidate)}
                      disabled={busyDocId === candidate.doc_id}
                    >
                      {busyDocId === candidate.doc_id ? "Verifying…" : "Verify"}
                    </button>
                  )}
                  {verdict && verified && (
                    <>
                      <span className="pill pill--ok">Verified</span>
                      <button
                        className="btn btn--primary btn--sm"
                        onClick={() => runResolve(mode === "add" ? "add" : "confirm", candidate)}
                        disabled={busyDocId === candidate.doc_id}
                      >
                        {mode === "add" ? "Attach authority" : "Attach as officer-confirmed"}
                      </button>
                    </>
                  )}
                  {verdict && !verified && (
                    <span className="pill pill--bad" title={verdict.reason}>
                      {verdict.verification_status} — cannot attach
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {mode === "resolve" && activeCitation && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
            <span style={{ color: "var(--ink-3)", fontSize: 13 }}>
              No matching record for “{activeCitation.citation}”?
            </span>
            <div style={{ flex: 1 }} />
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => runResolve("discard")}
              disabled={busyDocId === "discard"}
            >
              {busyDocId === "discard" ? "Discarding…" : "Discard citation"}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
